import { useState, useEffect, useContext, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import PulseLoader from "react-spinners/PulseLoader";

import DataTable from "examples/Tables/DataTable";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import PageviewIcon from "@mui/icons-material/Pageview";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import UploadIcon from "@mui/icons-material/Upload";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";
import { useForm, Controller } from "react-hook-form";
import qs from "query-string";

import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";
import { collectionSortOptions } from "utils/data";
import { compare } from "utils/helper";
import SearchIcon from "@mui/icons-material/Search";

import CreateCollectionModal from "pages/de/collection/components/CreateCollectionModal";
import ImportStyleModal2 from "pages/de/project/create-project/components/ImportStyleModal2";
import CollectionDetail from "pages/de/collection/detail";
import EmptyCollectionRecord from "pages/de/collection/components/EmptyCollectionRecord";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

const CollectionList = ({ sharedView = false }) => {
  const [collectionList, setCollectionList] = useState([]);
  const [filteredCollectionList, setFilteredCollectionList] = useState([]);
  const [totalNumber, setTotalNumber] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [detail, setDetail] = useState([]);
  const [orderOpt, setOrderOpt] = useState(collectionSortOptions[4]);
  const [searchStr, setSearchStr] = useState("");
  const [createCollectionVisible, setCreateCollectionVisible] = useState(false);
  const [editCollectionVisible, setEditCollectionVisible] = useState(false);
  const [deleteCollectionVisible, setDeleteCollectionVisible] = useState(false);
  const [importStyleVisible, setImportStyleVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [editCollection, setEditCollection] = useState({});

  const account = useContext(AccountContext);
  const addCollectionRef = useRef();
  const importStyleRef = useRef();

  const navigate = useNavigate();

  let location = useLocation();
  const url = location.href ? location.href : location.pathname + location.search;
  const urlObj = qs.parseUrl(url);
  const collectionId = urlObj?.query?.id;
  const action = urlObj?.query?.a;

  useEffect(() => {
    if (collectionId) {
      retrieveCollectionDetail(collectionId);
    }
  }, [collectionId, url]);

  useEffect(() => {
    if (account.id) {
      retrieveCollections();
    }
  }, [account.id, url]);

  useEffect(() => {
    if (action) {
      setCreateCollectionVisible(true);
    }
  }, []);

  const {
    tree,
    actions: { refreshCategoryTree, findNodeByID, findParents },
  } = useContext(CategoryContext);

  const retrieveCollections = async (sort = "") => {
    try {
      const { data } = await api.collections.list({ order: "-updated_at" });

      let collections = data["collections"] ? data["collections"] : [];

      collections = collections.map((x) => ({
        ...x,
        season: x?.metadata?.season?.value || "",
        year: x?.metadata?.year || "",
      }));
      setCollectionList(collections);
      setTotalNumber(collections.length);

      //filter based on pagination
      const sliceStart = pageSize * (page - 1);
      const sliceEnd = pageSize * page > collections.length ? collections.length : pageSize * page;
      const newFilteredList = collections.slice(sliceStart, sliceEnd);

      setFilteredCollectionList(newFilteredList);
    } catch (err) {
      toast.error(`Retrieve collection list failed`);
      console.log(err);
    }
  };

  const retrieveCollectionDetail = async () => {
    try {
      setPageLoading(true);
      const { data } = await api.collections.retrieve(urlObj?.query?.id);
      const collection = data["collections"] ? data["collections"] : [];
      if (collection.length > 0) {
        setDetail(collection[0]);
      }
      setPageLoading(false);
    } catch (err) {
      console.log(err);
      toast.error(`Retrieve collection detail failed`);
      setPageLoading(false);
    }
  };

  const handleFilterCollections = (page, pageSize, str = searchStr) => {
    const copyProjects = [...collectionList];
    let filteredList = copyProjects; //all projects

    if (str) {
      const strUpper = str.toUpperCase();
      const searchFields = ["title", "description"];
      filteredList = filteredList.filter((x) => {
        let result = false;
        for (let i = 0; i < searchFields.length; i++) {
          if (x[searchFields[i]]) {
            if (typeof x[searchFields[i]] == "string") {
              if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                result = true;
              }
            } else if (typeof x[searchFields[i]] == "object") {
              const text = JSON.stringify(x[searchFields[i]]);
              if (text.toUpperCase().includes(strUpper)) {
                result = true;
              }
            }
          }
        }
        return result;
      });
    }

    setTotalNumber(filteredList.length);

    let newPage = page;
    //filter based on pagination
    const sliceStart = pageSize * (newPage - 1);
    const sliceEnd =
      pageSize * newPage > filteredList.length ? filteredList.length : pageSize * newPage;
    const newFilteredList = filteredList.slice(sliceStart, sliceEnd);
    setFilteredCollectionList(newFilteredList);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleFilterCollections(newPage, pageSize, searchStr);
  };

  const handleChangeRowsPerPage = (option) => {
    const newPageSize = parseInt(option.value, 10);
    setPageSize(newPageSize);
    setPage(1);
    handleFilterCollections(1, newPageSize, searchStr);
  };

  const pageCount = parseInt(Math.ceil(totalNumber / pageSize), 10);
  const pageOptions = [];
  for (let i = 0; i < pageCount; i++) {
    pageOptions.push(i);
  }
  const entriesStart = pageSize * (page - 1) + 1;
  const entriesEnd = pageSize * page > totalNumber ? totalNumber : pageSize * page;

  const handleSearchChange = (str) => {
    let formattedStr = "";
    if (str) {
      formattedStr = str.trim();
    }
    setSearchStr(formattedStr);
    handleFilterCollections(page, pageSize, formattedStr);
  };

  const handleOnSortChange = (e) => {
    setOrderOpt(e);
    let sortStr = e?.value;
    const newFilteredCollectionList = filteredCollectionList.sort((a, b) => compare(a, b, sortStr));
    setFilteredCollectionList(newFilteredCollectionList);
  };

  const handleAfterOk = (newCollection) => {
    let id = newCollection?.data?.collection?.id;
    if (id) {
      navigate(`/workspace/collections?id=${id}`);
    }
  };

  const getActionsMenu = (item) => {
    const actions = [
      {
        label: <SuiTypography variant="body">Edit Collection</SuiTypography>,
        icon: <EditRoundedIcon fontSize={"small"} />,
        onClick: () => {
          setEditCollectionVisible(true);
          setEditCollection(item);
        },
      },
      {
        label: (
          <SuiTypography variant="body" color="error">
            Delete Collection{" "}
          </SuiTypography>
        ),
        onClick: () => {
          setDeleteCollectionVisible(true);
          setEditCollection(item);
        },
        icon: <DeleteForeverRoundedIcon fontSize={"small"} color="error" />,
      },
    ];
    return actions;
  };

  //List/table view
  const getCollectionTableRow = (item) => {
    const collectionMeta = item?.metadata || {};
    return {
      key: item.id,
      moodboard: item?.moodboard?.[0]?.img,

      name: item.title,
      created_at: item.created_at,
      updated_at: item.updated_at,
      season: collectionMeta?.season,
      year: collectionMeta?.year,
      brand: collectionMeta?.brand,
      styles_number: item.products.length,
      action: item,
    };
  };

  const columns = [
    {
      Header: "Moodboard",
      accessor: "moodboard",
      align: "center",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <Link to={`/workspace/collections?id=${row?.original?.key}`}>
            <ImageCardWrapper data={value} mode={"normal"} height={60} width={60} iconMode />
          </Link>
        );
      },
    },
    {
      Header: "Name",
      accessor: "name",
      isSorted: false,
      canSort: false,
    },

    {
      Header: "Season",
      accessor: "season",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return value?.label;
      },
    },

    {
      Header: "# of Styles",
      accessor: "styles_number",
      isSorted: false,
      canSort: false,
    },
    {
      Header: "Year",
      accessor: "year",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return value ? moment(value).format("YYYY") : "";
      },
    },
    {
      Header: "Updated Date",
      accessor: "updated_at",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="View Details">
            <SuiBox component={Link} to={`/workspace/collections?id=${value.id}`}>
              <ManageSearchIcon fontSize={"small"} color="dark" />
            </SuiBox>
          </Tooltip>
          |{" "}
          <Tooltip title="Edit">
            <SuiBox>
              <EditRoundedIcon
                fontSize={"small"}
                color="dark"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditCollectionVisible(true);
                  setEditCollection(value);
                }}
              />
            </SuiBox>
          </Tooltip>{" "}
          |{" "}
          <Tooltip title="Import Styles">
            <SuiBox>
              <AddPhotoAlternateIcon
                fontSize={"small"}
                sx={{ cursor: "pointer" }}
                color="dark"
                onClick={(e) => {
                  e.stopPropagation();
                  setImportStyleVisible(true);
                  setEditCollection(value);
                }}
              />
            </SuiBox>
          </Tooltip>
          |{" "}
          <Tooltip title="Delete">
            <SuiBox>
              <DeleteForeverRoundedIcon
                fontSize={"small"}
                color="error"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();

                  setDeleteCollectionVisible(true);
                  setEditCollection(value);
                }}
              />
            </SuiBox>
          </Tooltip>
        </SuiBox>
      ),
    },
  ];
  const handleRowClick = (row) => {
    navigate(`/workspace/collections?id=${row?.original?.key}`);
    //window.open(`/workspace/collections?id=${row?.original?.key}`, "_blank");
  };
  const rows = filteredCollectionList.map((item) => getCollectionTableRow(item));

  return collectionId ? (
    <CollectionDetail
      detail={detail}
      editable={false}
      handleRefresh={() => retrieveCollectionDetail(collectionId)}
      pageLoading={pageLoading}
      sharedView={sharedView}
    />
  ) : (
    <SuiBox my={1}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" color="info">
          Uploading supporting files...
        </SuiTypography>
      </Backdrop>

      <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} mr={1}>
        <SuiButton
          variant="gradient"
          color={"dark"}
          onClick={() => setCreateCollectionVisible(true)}
          size={"none"}
          sx={{ py: 0, px: 2, wordBreak: "break-word" }}
        >
          Create New Collection
        </SuiButton>

        <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
          <SuiBox>
            <Card sx={{ borderRadius: 2, width: 200 }}>
              <SuiSelect
                options={collectionSortOptions}
                placeholder="Sort"
                isClearable
                onChange={handleOnSortChange}
                value={orderOpt}
              />
            </Card>
          </SuiBox>
          <SuiBox>
            <SuiInput
              placeholder="Search Keywords"
              type="text"
              onChange={(e) => handleSearchChange(e?.target?.value)}
              icon={{
                direction: "right",
                component: (
                  <SearchIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSearchChange(searchStr)}
                  />
                ),
              }}
            />{" "}
          </SuiBox>
        </SuiBox>
      </SuiBox>

      <div>
        <Card sx={{ p: 3 }}>
          {rows.length > 0 ? (
            <DataTable
              table={{ columns, rows: rows }}
              entriesPerPage={{ defaultValue: pageSize }}
              showTotalEntries={false}
              pagination={false}
              noEndBorder
              handleRowClick={handleRowClick}
              isSorted={false}
            />
          ) : (
            <EmptyCollectionRecord />
          )}
        </Card>
      </div>

      <SuiBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={3}
      >
        <div className="flex items-center">
          <SuiBox mb={{ xs: 3, sm: 0 }}>
            <SuiTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {totalNumber} entries
            </SuiTypography>
          </SuiBox>

          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiSelect
              defaultValue={{ value: pageSize, label: pageSize }}
              options={[4, 10, 20, 50].map((entry) => ({ value: entry, label: entry }))}
              onChange={handleChangeRowsPerPage}
              size="small"
            />

            <SuiTypography variant="caption" color="secondary">
              entries per page
            </SuiTypography>
          </SuiBox>
        </div>
        <Pagination
          variant={"outlined"}
          color={"info"}
          count={pageOptions.length}
          page={page}
          onChange={handleChangePage}
        />
      </SuiBox>
      {createCollectionVisible ? (
        <CreateCollectionModal
          handleClose={() => setCreateCollectionVisible(false)}
          visible={createCollectionVisible}
          isEdit={false}
          handleAfterOk={handleAfterOk}
          handleRefresh={retrieveCollections}
        />
      ) : null}
      {editCollectionVisible && editCollection?.id ? (
        <CreateCollectionModal
          handleClose={() => {
            setEditCollectionVisible(false);
            setEditCollection({});
          }}
          visible={editCollectionVisible}
          isEdit={true}
          collection={editCollection}
          handleRefresh={retrieveCollections}
        />
      ) : null}
      {editCollection?.id && deleteCollectionVisible ? (
        <Prompt
          heading="Delete Collection"
          text={`Are you sure you want to delete "${editCollection.title}"?
          The Styles from this Collection will be moved to your Style Master.`}
          visible={deleteCollectionVisible}
          okText="Delete"
          okColor="error"
          handleOk={() =>
            api.collections
              .delete(editCollection.id)
              .then(() => {
                toast.success(
                  `Your Collection "${editCollection.title}" has been successfully deleted!`
                );
                retrieveCollections();
                setDeleteCollectionVisible(false);
              })
              .catch((err) => {
                console.log(err);
                toast.error(`Your Collection could not be deleted!`);
              })
          }
          handleClose={() => setDeleteCollectionVisible(false)}
        />
      ) : null}
      {importStyleVisible && editCollection?.id ? (
        <ImportStyleModal2
          handleClose={() => setImportStyleVisible(false)}
          visible={importStyleVisible}
          isEdit={false}
          categoryTree={tree}
          ref={importStyleRef}
          collection={editCollection}
          handleRefresh={() => retrieveCollections()}
        />
      ) : null}
    </SuiBox>
  );
};

export default CollectionList;
